import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { kitchenMaterialBinding, type SceneCompositor } from '@cfg/render-2d';
import {
  pick,
  tierFor,
  type KitchenCatalog,
  type KitchenConfig,
  type KitchenMaterial,
  type Locale,
  type ZoneKey,
} from '@cfg/shared';
import {
  Alert,
  Button,
  DownloadIcon,
  FileIcon,
  GlobeIcon,
  IconButton,
  LayersIcon,
  MoonIcon,
  ResetIcon,
  Segmented,
  ShareIcon,
  Spinner,
  SunIcon,
  useToast,
} from '@cfg/ui';
import { analytics } from './analytics';
import { downloadBlob, fetchCatalog, loadConfiguration } from './api';
import { ExportDialog, ShareDialog, SummaryDialog } from './components/Dialogs';
import { MaterialBrowser } from './components/MaterialBrowser';
import { Viewer, type ViewerHandle } from './components/Viewer';
import { ZoneList } from './components/ZoneList';
import { I18nContext, makeI18n, rememberLocale, useI18n } from './i18n';
import { rovingKeyDown } from './roving';
import {
  configFromSearch,
  configToSearch,
  effectiveSelections,
  initialConfig,
  sanitizeConfig,
} from './state';

/**
 * The query string the page was opened with. The URL is rewritten as soon as the configurator
 * mounts (a shared `?c=CODE` link is replaced by the readable form), so read it only from here.
 */
const INITIAL_SEARCH = typeof location === 'undefined' ? '' : location.search;

/**
 * The other parts of the studio, reachable from the top bar. In development each app has its
 * own port; a deployment that serves everything under one domain sets these to paths
 * (`/doors`, `/demo`, `/admin`) at build time.
 */
const links = {
  kitchen: import.meta.env.VITE_KITCHEN_URL || '/',
  doors: import.meta.env.VITE_DOORS_URL || 'http://localhost:5174',
  demo: import.meta.env.VITE_DEMO_URL || 'http://localhost:5177',
  admin: import.meta.env.VITE_ADMIN_URL || 'http://localhost:5175',
};

export function App({ initialLocale }: { initialLocale: Locale }) {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const i18n = useMemo(() => makeI18n(locale), [locale]);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = i18n.dir;
    document.title = i18n.t('appTitle');
    rememberLocale(locale);
  }, [locale, i18n]);

  const catalogQuery = useQuery({
    queryKey: ['kitchen-catalog'],
    queryFn: fetchCatalog,
    staleTime: 60_000,
  });

  return (
    <I18nContext.Provider value={i18n}>
      <a className="cfg-skip-link" href="#material-panel">
        {i18n.t('materials')}
      </a>
      {catalogQuery.data ? (
        <Configurator
          catalog={catalogQuery.data}
          onToggleLocale={() => setLocale(locale === 'he' ? 'en' : 'he')}
        />
      ) : catalogQuery.isError ? (
        <main className="center-screen">
          <Alert tone="error">{i18n.t('errorCatalog')}</Alert>
          <Button variant="primary" onClick={() => void catalogQuery.refetch()}>
            {i18n.t('retry')}
          </Button>
        </main>
      ) : (
        <main className="center-screen">
          <Spinner label={i18n.t('loading')} />
        </main>
      )}
    </I18nContext.Provider>
  );
}

function Configurator({
  catalog,
  onToggleLocale,
}: {
  catalog: KitchenCatalog;
  onToggleLocale: () => void;
}) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [config, setConfig] = useState<KitchenConfig>(
    () => configFromSearch(catalog, location.search) ?? initialConfig(catalog),
  );
  const scene = catalog.scenes.find((s) => s.key === config.sceneKey) ?? catalog.scenes[0]!;
  // scene zones in catalog order (the manifest order follows the renderer)
  const zones = useMemo(
    () => catalog.zones.map((z) => z.key).filter((k) => scene.manifest.zones.includes(k)),
    [catalog, scene],
  );
  const [activeZone, setActiveZone] = useState<ZoneKey>(zones[0]!);
  const [hoverZone, setHoverZone] = useState<ZoneKey | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'export' | 'share' | 'summary' | null>(null);
  const viewerRef = useRef<ViewerHandle>(null);
  const selections = useMemo(() => effectiveSelections(catalog, config), [catalog, config]);

  // shared link (?c=CODE)
  useEffect(() => {
    const code = new URLSearchParams(location.search).get('c');
    if (!code) return;
    loadConfiguration(code)
      .then((loaded) => setConfig(sanitizeConfig(catalog, loaded)))
      .catch(() => toast(t('configNotFound'), 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep the URL in sync so reloads and bookmarks keep the configuration
  useEffect(() => {
    const search = configToSearch(catalog, config);
    if (search !== location.search) history.replaceState(null, '', `${location.pathname}${search}`);
  }, [catalog, config]);

  useEffect(() => {
    if (!zones.includes(activeZone)) setActiveZone(zones[0]!);
  }, [zones, activeZone]);

  // usage analytics (sent only after consent)
  const tracked = useRef<{ scene: string; lighting: string } | null>(null);
  useEffect(() => {
    const last = tracked.current;
    if (!last)
      analytics.track('configurator_opened', {
        scene: config.sceneKey,
        shared: new URLSearchParams(INITIAL_SEARCH).has('c'),
      });
    else if (last.scene !== config.sceneKey)
      analytics.track('scene_changed', { scene: config.sceneKey });
    else if (last.lighting !== config.lighting)
      analytics.track('lighting_changed', { lighting: config.lighting });
    tracked.current = { scene: config.sceneKey, lighting: config.lighting };
  }, [config.sceneKey, config.lighting]);

  const selectMaterial = useCallback(
    (material: KitchenMaterial) => {
      setPending(material.id);
      setConfig((c) => ({ ...c, selections: { ...c.selections, [activeZone]: material.id } }));
      analytics.track('material_selected', { zone: activeZone, code: material.code });
    },
    [activeZone],
  );

  useEffect(() => {
    if (pending) {
      const timer = setTimeout(() => setPending(null), 400);
      return () => clearTimeout(timer);
    }
  }, [pending, selections]);

  const preload = useCallback(
    (material: KitchenMaterial) => {
      const compositor = (window as unknown as { __compositor?: SceneCompositor }).__compositor;
      if (!compositor) return;
      const binding = kitchenMaterialBinding(
        material,
        activeZone,
        window.innerWidth < 900 ? 1024 : 2048,
      );
      if (binding.textureUrl) compositor.preload(binding.textureUrl);
    },
    [activeZone],
  );

  const pickZone = useCallback((zone: ZoneKey) => {
    setActiveZone(zone);
    document
      .getElementById('material-panel')
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  const onViewerError = useCallback(
    (message: string) => toast(message || t('errorGeneric'), 'error'),
    [toast, t],
  );

  const downloadImage = async () => {
    try {
      const blob = await viewerRef.current!.exportImage();
      downloadBlob(blob, `${scene.key}-${config.lighting}.jpg`);
      analytics.track('image_downloaded', { scene: scene.key, lighting: config.lighting });
    } catch {
      toast(t('errorGeneric'), 'error');
    }
  };

  const reset = () => setConfig((c) => ({ ...c, selections: {} }));

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="brand__mark" aria-hidden>
            <LayersIcon size={18} />
          </span>
          <span className="brand__text">
            <strong>{t('appTitle')}</strong>
            <span className="muted">{t('appSubtitle')}</span>
          </span>
        </div>
        <nav className="studio-nav" aria-label={t('studioNav')}>
          <a href={links.kitchen} aria-current="page">
            {t('navKitchens')}
          </a>
          <a href={links.doors}>{t('navDoors')}</a>
          <a href={links.demo}>{t('navDemo')}</a>
          <a href={links.admin}>{t('navAdmin')}</a>
        </nav>
        <div className="topbar__actions">
          <Button
            size="sm"
            variant="ghost"
            icon={<LayersIcon size={16} />}
            onClick={() => setDialog('summary')}
          >
            <span className="hide-sm">{t('summary')}</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<ShareIcon size={16} />}
            onClick={() => setDialog('share')}
          >
            <span className="hide-sm">{t('share')}</span>
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon={<FileIcon size={16} />}
            onClick={() => setDialog('export')}
          >
            <span className="hide-xs">{t('exportPdf')}</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<GlobeIcon size={16} />}
            onClick={onToggleLocale}
            aria-label={t('switchLanguage')}
          >
            {locale === 'he' ? 'EN' : 'עב'}
          </Button>
        </div>
      </header>

      <div className="studio-intro">
        <div>
          <p className="studio-eyebrow">
            {locale === 'he' ? 'סטודיו לחומרים' : 'THE MATERIAL STUDIO'}
          </p>
          <h1>{locale === 'he' ? 'מקום לכל מה שאתם.' : 'A space, distinctly yours.'}</h1>
          <p>
            {locale === 'he'
              ? 'גלו חומרים, שנו את האור ומצאו את השילוב שלכם.'
              : 'Explore textures, follow the light, and find your perfect combination.'}
          </p>
        </div>
        <span className="studio-project">
          {locale === 'he' ? 'עיצוב מטבח' : 'Kitchen collection'} <span> / 01</span>
        </span>
      </div>

      <main className="layout">
        <section className="stage" aria-label={t('scene')}>
          <div className="stage__title">
            <span>
              <span className="live-dot" />
              {pick(scene.name, locale)}
            </span>
            <span>{locale === 'he' ? 'תצוגה אינטראקטיבית' : 'Interactive preview'}</span>
          </div>
          <Viewer
            ref={viewerRef}
            catalog={catalog}
            sceneKey={scene.key}
            lighting={config.lighting}
            selections={selections}
            highlight={hoverZone}
            onHoverZone={setHoverZone}
            onPickZone={pickZone}
            onError={onViewerError}
          />
          <div className="stage__bar">
            <div
              className="scenes"
              role="radiogroup"
              aria-label={t('scenes')}
              onKeyDown={(e) => rovingKeyDown(e, { selector: '[role="radio"]', activate: true })}
            >
              {catalog.scenes.map((s) => {
                const tier = tierFor(s.manifest, 400);
                return (
                  <button
                    key={s.key}
                    type="button"
                    role="radio"
                    aria-checked={s.key === scene.key}
                    tabIndex={s.key === scene.key ? 0 : -1}
                    className="scene-thumb"
                    onClick={() =>
                      setConfig((c) => ({
                        ...c,
                        sceneKey: s.key,
                        lighting: s.manifest.lightings[c.lighting] ? c.lighting : 'day',
                      }))
                    }
                  >
                    <img
                      src={`${s.baseUrl}/${tier.dir}/${s.manifest.preview}`}
                      alt=""
                      loading="lazy"
                    />
                    <span>{pick(s.name, locale)}</span>
                  </button>
                );
              })}
            </div>
            <div className="stage__tools">
              {scene.manifest.lightings.night && (
                <Segmented
                  label={t('lighting')}
                  value={config.lighting}
                  onChange={(lighting) => setConfig((c) => ({ ...c, lighting }))}
                  options={[
                    { value: 'day', label: t('day'), icon: <SunIcon size={16} /> },
                    { value: 'night', label: t('night'), icon: <MoonIcon size={16} /> },
                  ]}
                />
              )}
              <IconButton
                size="sm"
                label={t('downloadImage')}
                icon={<DownloadIcon size={18} />}
                onClick={() => void downloadImage()}
              />
              <IconButton
                size="sm"
                label={t('reset')}
                icon={<ResetIcon size={18} />}
                onClick={reset}
              />
            </div>
          </div>
          <p className="stage__hint muted">
            {t('hoverHint')} {t('disclaimer')}
          </p>
        </section>

        <aside className="panel" aria-label={t('materials')}>
          <div className="panel__intro">
            <span className="studio-eyebrow">
              {locale === 'he' ? 'התאמה אישית' : 'MAKE IT YOURS'}
            </span>
            <h2>{locale === 'he' ? 'חומרים וגימורים' : 'Materials & finishes'}</h2>
          </div>
          <div className="panel__zones">
            <h2 className="panel__heading">{t('chooseZone')}</h2>
            <ZoneList
              catalog={catalog}
              zones={zones}
              active={activeZone}
              selections={selections}
              onSelect={setActiveZone}
              onHover={setHoverZone}
            />
          </div>
          <MaterialBrowser
            key={activeZone}
            catalog={catalog}
            zone={activeZone}
            selected={selections[activeZone]}
            pending={pending}
            onSelect={selectMaterial}
            onPreload={preload}
          />
        </aside>
      </main>
      <footer className="studio-footer">
        <span>FORM / {locale === 'he' ? 'סטודיו לתכנון' : 'Design studio'}</span>
        <span>
          {locale === 'he'
            ? 'חומרים. אור. אפשרויות.'
            : 'Considered materials. Endless possibilities.'}
        </span>
      </footer>

      <ExportDialog
        open={dialog === 'export'}
        onClose={() => setDialog(null)}
        catalog={catalog}
        config={config}
      />
      <ShareDialog open={dialog === 'share'} onClose={() => setDialog(null)} config={config} />
      <SummaryDialog
        open={dialog === 'summary'}
        onClose={() => setDialog(null)}
        catalog={catalog}
        zones={zones}
        selections={selections}
      />
    </div>
  );
}
